<template>
  <div class="news-page-container">
    <!-- 刷新设置和新闻源切换区域 -->
    <div class="refresh-settings">
      <div class="refresh-controls">
        <button
          @click="toggleAutoRefresh"
          class="auto-refresh-toggle"
          :class="{ 'active': isAutoRefreshEnabled }"
        >
          {{ isAutoRefreshEnabled ? '停止自动刷新' : '开启自动刷新' }}
        </button>
        <input
          v-model.number="refreshRate"
          type="number"
          placeholder="刷新间隔时间（秒）"
          class="refresh-input"
          :disabled="!isAutoRefreshEnabled"
          @change="updateRefreshRate"
        />
        <button
          @click="updateRefreshRate"
          class="refresh-button"
          :disabled="!isAutoRefreshEnabled"
        >
          更新刷新时间
        </button>

        <!-- 新闻源选择 -->
        <select
          v-model="selectedSource"
          class="source-selector"
          @change="onSourceChange"
        >
          <option value="">全部新闻源</option>
          <option
            v-for="source in availableSources"
            :key="source"
            :value="source"
          >
            {{ getSourceName(source) }}
          </option>
        </select>
      </div>
      <div class="page-info">
        已加载 {{ currentPage * pageSize }} 条新闻，共 {{ totalCount }} 条
        <span v-if="selectedSource"> ({{ getSourceName(selectedSource) }})</span>
      </div>
    </div>

    <!-- 瀑布流新闻列表 -->
    <div class="masonry-container">
      <div
        v-for="column in columns"
        :key="column"
        class="masonry-column"
      >
        <div
          v-for="news in getColumnNews(column)"
          :key="news.id"
          class="news-item"
          @click="openInIframe(news.shareurl)"
        >
          <div class="news-header">
            <h3>{{ news.title || news.content || '无标题' }}</h3>
          </div>
          <div class="news-content">
            <p>{{ news.content || news.brief || '无内容' }}</p>
            <div class="news-footer">
              <span class="news-source">来源: {{ news.source || '未知' }}</span>
              <span class="news-time">{{ formatTime(news.createtime) }}</span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- 加载更多按钮 -->
    <div v-if="hasMore" class="load-more-container">
      <button
        @click="loadMore"
        class="load-more-button"
        :disabled="loading"
      >
        {{ loading ? '加载中...' : '加载更多' }}
      </button>
    </div>

    <!-- 加载状态 -->
    <div v-if="loading && currentPage === 1" class="loading">加载中...</div>

    <!-- 无数据提示 -->
    <div v-if="!loading && newsList.length === 0" class="no-data">
      暂无新闻数据
    </div>

    <!-- 全部加载完成提示 -->
    <div v-if="!hasMore && newsList.length > 0" class="no-more-data">
      已加载全部新闻数据
    </div>

    <!-- 移动端iframe查看器 -->
    <MobileIframeViewer
      v-if="showIframe"
      :show-iframe="showIframe"
      :iframe-src="iframeSrc"
      @close="closeIframe"
    />
  </div>
</template>

<script>
import axios from 'axios';
import MobileIframeViewer from '@/components/MobileIframeViewer.vue';

export default {
  components: {
    MobileIframeViewer
  },
  name: 'NewsPage',
  data() {
    return {
      newsList: [],
      refreshInterval: null,
      refreshRate: 60,
      showIframe: false,
      iframeSrc: '',
      loading: false,
      currentPage: 1,
      pageSize: 20,
      totalCount: 0,
      hasMore: true,
      columns: 3, // 瀑布流列数
      isAutoRefresh: false, // 是否正在自动刷新
      isAutoRefreshEnabled: false, // 是否启用自动刷新功能
      selectedSource: '', // 当前选中的新闻源
      availableSources: [], // 可用的新闻源列表
      sourceNames: { // 新闻源名称映射
        '10jqka': '同花顺',
        'yicai': '第一财经',
        'fenghuang': '凤凰财经',
        'jinrongjie': '金融界',
        'sina': '新浪财经',
        'yuncaijing': '云财经',
        'cls': '财联社',
        'eastmoney': '东方财富',
        'wallstreetcn': '华尔街见闻'
      }
    };
  },
  computed: {
    // 根据屏幕宽度动态调整列数
    columnCount() {
      if (window.innerWidth < 768) return 1;
      if (window.innerWidth < 1024) return 2;
      return this.columns;
    }
  },
  created() {
    this.fetchAvailableSources(); // 获取可用新闻源
    this.fetchNews();
    // 默认不启动自动刷新
    // this.startRefresh(); // 注释掉这行，默认不自动刷新
    window.addEventListener('resize', this.handleResize);
    window.addEventListener('scroll', this.handleScroll);
  },
  beforeUnmount() {
    this.stopRefresh();
    window.removeEventListener('resize', this.handleResize);
    window.removeEventListener('scroll', this.handleScroll);
  },
  methods: {
    // 获取可用的新闻源列表
    async fetchAvailableSources() {
      try {
        const response = await axios.get('/api/news/sources');
        if (response.data.code === 200) {
          this.availableSources = response.data.data || [];
        } else {
          console.error('获取新闻源失败:', response.data.message);
          // 使用默认新闻源列表
          this.availableSources = ['10jqka', 'yicai', 'fenghuang', 'jinrongjie', 'sina', 'yuncaijing', 'cls', 'eastmoney', 'wallstreetcn'];
        }
      } catch (error) {
        console.error('Error fetching sources:', error);
        // 使用默认新闻源列表
        this.availableSources = ['10jqka', 'yicai', 'fenghuang', 'jinrongjie', 'sina', 'yuncaijing', 'cls', 'eastmoney', 'wallstreetcn'];
      }
    },

    // 获取新闻源名称
    getSourceName(source) {
      return this.sourceNames[source] || source;
    },

    // 新闻源切换事件
    onSourceChange() {
      this.resetPagination(); // 重置分页
      this.fetchNews(); // 重新获取新闻
    },

    // 重置分页状态
    resetPagination() {
      this.newsList = [];
      this.currentPage = 1;
      this.totalCount = 0;
      this.hasMore = true;
    },

    async fetchNews(isLoadMore = false) {
      if (this.loading) return;

      this.loading = true;
      try {
        const page = isLoadMore ? this.currentPage + 1 : 1;
        const offset = (page - 1) * this.pageSize;

        const response = await axios.get('/api/news', {
          params: {
            limit: this.pageSize,
            offset: offset,
            source: this.selectedSource || undefined // 如果未选择任何源，不传此参数
          }
        });

        if (response.data.code === 200) {
          const newNews = response.data.data || [];
          this.totalCount = response.data.total || 0;

          if (isLoadMore) {
            this.newsList = [...this.newsList, ...newNews];
            this.currentPage = page;
          } else {
            this.newsList = newNews;
            this.currentPage = 1;
          }

          // 检查是否还有更多数据
          this.hasMore = this.newsList.length < this.totalCount;
        } else {
          console.error('获取新闻失败:', response.data.message);
          if (!isLoadMore) {
            this.newsList = [];
          }
        }
      } catch (error) {
        console.error('Error fetching news:', error);
        if (!isLoadMore) {
          this.newsList = [];
        }
      } finally {
        this.loading = false;
      }
    },

    // 加载更多数据
    loadMore() {
      if (!this.loading && this.hasMore) {
        this.fetchNews(true);
      }
    },

    // 瀑布流布局：将新闻分配到各列
    getColumnNews(columnIndex) {
      const columnCount = this.columnCount;
      return this.newsList.filter((_, index) => index % columnCount === columnIndex);
    },

    // 滚动加载检测
    handleScroll() {
      if (this.isAutoRefresh) return; // 自动刷新模式下禁用滚动加载

      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      const windowHeight = window.innerHeight;
      const documentHeight = document.documentElement.scrollHeight;

      // 距离底部100px时触发加载
      if (scrollTop + windowHeight >= documentHeight - 100) {
        this.loadMore();
      }
    },

    // 窗口大小变化处理
    handleResize() {
      // 防抖处理，避免频繁重排
      clearTimeout(this.resizeTimer);
      this.resizeTimer = setTimeout(() => {
        this.$forceUpdate(); // 触发重新计算列数
      }, 200);
    },

    // 切换自动刷新状态
    toggleAutoRefresh() {
      this.isAutoRefreshEnabled = !this.isAutoRefreshEnabled;
      if (this.isAutoRefreshEnabled) {
        this.startRefresh();
      } else {
        this.stopRefresh();
      }
    },

    startRefresh() {
      if (this.refreshInterval) {
        clearInterval(this.refreshInterval);
      }
      this.refreshInterval = setInterval(() => {
        this.isAutoRefresh = true;
        this.fetchNews(false);
        setTimeout(() => {
          this.isAutoRefresh = false;
        }, 1000);
      }, this.refreshRate * 1000);
    },

    stopRefresh() {
      if (this.refreshInterval) {
        clearInterval(this.refreshInterval);
        this.refreshInterval = null;
      }
    },

    updateRefreshRate() {
      if (this.refreshRate < 10) {
        this.refreshRate = 10;
        alert('刷新间隔不能小于10秒');
      }
      if (this.isAutoRefreshEnabled) {
        this.stopRefresh();
        this.startRefresh();
      }
    },

    openInIframe(url) {
      if (url) {
        this.iframeSrc = url;
        this.showIframe = true;
      }
    },

    closeIframe() {
      this.showIframe = false;
      this.iframeSrc = '';
    },

    formatTime(timeStr) {
      if (!timeStr) return '未知时间';
      try {
        const date = new Date(timeStr);
        return date.toLocaleString('zh-CN');
      } catch (error) {
        return timeStr;
      }
    }
  },
};
</script>

<style scoped>
/* 添加新闻源选择器样式 */
.source-selector {
  padding: 8px 12px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;
  background: white;
  cursor: pointer;
  min-width: 120px;
}

.source-selector:focus {
  outline: none;
  border-color: #409eff;
  box-shadow: 0 0 0 2px rgba(64, 158, 255, 0.2);
}

/* 其他现有样式保持不变 */
.news-page-container {
  padding: 20px;
  max-width: 1400px;
  margin: 0 auto;
}

.refresh-settings {
  margin-bottom: 30px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 15px;
  padding: 15px;
  background: #f5f5f5;
  border-radius: 8px;
  flex-wrap: wrap;
}

.refresh-controls {
  display: flex;
  align-items: center;
  gap: 10px;
}

.auto-refresh-toggle {
  padding: 8px 16px;
  background: #67c23a;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
  transition: all 0.3s ease;
}

.auto-refresh-toggle:hover {
  background: #5daf34;
}

.auto-refresh-toggle.active {
  background: #f56c6c;
}

.auto-refresh-toggle.active:hover {
  background: #e64c4c;
}

.refresh-input {
  padding: 8px 12px;
  border: 1px solid #ddd;
  border-radius: 4px;
  width: 200px;
  font-size: 14px;
}

.refresh-input:focus {
  outline: none;
  border-color: #409eff;
  box-shadow: 0 0 0 2px rgba(64, 158, 255, 0.2);
}

.refresh-input:disabled {
  background-color: #f5f5f5;
  color: #999;
  cursor: not-allowed;
}

.refresh-button {
  padding: 8px 16px;
  background: #409eff;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
  transition: background 0.3s;
}

.refresh-button:hover:not(:disabled) {
  background: #337ecc;
}

.refresh-button:disabled {
  background: #ccc;
  cursor: not-allowed;
}

.page-info {
  font-size: 14px;
  color: #666;
  margin-left: auto;
}

/* 瀑布流布局 */
.masonry-container {
  display: flex;
  gap: 20px;
  margin-bottom: 30px;
}

.masonry-column {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 20px;
}

/* 响应式列数调整 */
@media (max-width: 767px) {
  .masonry-container {
    flex-direction: column;
  }
}

@media (min-width: 768px) and (max-width: 1023px) {
  .masonry-container {
    gap: 15px;
  }
}

.news-item {
  background: white;
  border-radius: 8px;
  padding: 20px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  cursor: pointer;
  transition: all 0.3s ease;
  border: 1px solid #e8e8e8;
  break-inside: avoid; /* 防止内容被分割 */
}

.news-item:hover {
  transform: translateY(-4px);
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.15);
  border-color: #409eff;
}

.news-header {
  margin-bottom: 12px;
}

.news-header h3 {
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  color: #333;
  line-height: 1.4;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.news-content {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.news-content p {
  margin: 0;
  font-size: 14px;
  color: #666;
  line-height: 1.5;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.news-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 12px;
  color: #999;
}

.news-source {
  font-weight: 500;
}

.news-time {
  font-style: italic;
}

/* 加载更多按钮 */
.load-more-container {
  text-align: center;
  margin: 30px 0;
}

.load-more-button {
  padding: 12px 24px;
  background: #409eff;
  color: white;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-size: 16px;
  transition: all 0.3s ease;
  min-width: 120px;
}

.load-more-button:hover:not(:disabled) {
  background: #337ecc;
  transform: translateY(-2px);
}

.load-more-button:disabled {
  background: #ccc;
  cursor: not-allowed;
  transform: none;
}

.loading {
  text-align: center;
  padding: 40px;
  font-size: 16px;
  color: #666;
}

.no-data {
  text-align: center;
  padding: 40px;
  font-size: 16px;
  color: #999;
  background: #f9f9f9;
  border-radius: 8px;
}

.no-more-data {
  text-align: center;
  padding: 20px;
  font-size: 14px;
  color: #999;
  background: #f0f0f0;
  border-radius: 6px;
  margin-top: 20px;
}
</style>
